import { useRef } from 'react'
import isUndefined from 'lodash/isUndefined'
import { serializeSwrKey } from '../cache-key'
import { getCurrentStateThatSWRShouldReturn } from '../utils'

export const name = 'pagination'

// This is a SWR middleware for keeping the data even if key changes.
export const pagination = (useSWRNext) => {
	return (key, fetcher, config) => {
		const memoizedKeys = useRef({})
		const previousKey = useRef()

		// Actual SWR hook.
		const swr = useSWRNext(key, fetcher, config)

		if (!key) return swr

		const currentKey = serializeSwrKey(key)

		const { pagination: { enabled, page, infinite, key: pagKey } = {} } = config

		const serializedPagKey = serializeSwrKey(pagKey)

		if (previousKey.current !== serializedPagKey) {
			// Reset the memoized keys.
			memoizedKeys.current = {}
			previousKey.current = serializedPagKey
		}

		if (!memoizedKeys.current[serializedPagKey] || !infinite) {
			memoizedKeys.current[serializedPagKey] = {}
		}

		if (enabled && page) {
			memoizedKeys.current[serializedPagKey][page] = currentKey
		}

		let data

		if (enabled) {
			const pagesData = Object.entries(memoizedKeys.current[serializedPagKey])
				.sort(([pageA], [pageB]) => pageA - pageB)
				.map(([page, pageKey]) => {
					const currentStateData = getCurrentStateThatSWRShouldReturn({ key: pageKey, config })

					function addPage(entry, page) {
						if (typeof entry === 'object') {
							return { ...entry, __page: parseInt(page) }
						} else {
							return entry
						}
					}

					if (currentStateData) {
						return Array.isArray(currentStateData)
							? currentStateData.map((entry) => addPage(entry, page))
							: addPage(currentStateData, page)
					} else {
						return currentStateData
					}
				})
				.filter((pageData) => !isUndefined(pageData))

			// if no data found in cache or fallback, return undefined
			if (pagesData.length > 0) {
				data = pagesData.reduce((acc, pageData) => {
					return [...acc, ...(Array.isArray(pageData) ? pageData : [pageData])]
				}, [])
			} else {
				data = undefined
			}
		} else {
			data = swr.data
		}

		return { ...swr, data, hasPagination: enabled }
	}
}
